import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createCanteen } from "../../../redux/actions/canteenActions";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";
import {
  ArrowLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  Building2,
  Send,
  Home,
  Sparkles,
  FileText,
  Star,
  ClipboardCheck,
  Info,
  Camera,
} from "lucide-react";

const SectionDKitchenDining = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const { loading, error: apiError } = useSelector((state) => state.canteen);

  const surveyId = localStorage.getItem("survey_id") || "defaultSurvey";

  const [form, setForm] = useState({
    d1: "",
    d2: {
      cleanUtensils: false,
      cleanCounters: false,
      longHandleSpoons: false,
      foodCovered: false,
      notAllConditions: false,
    },
    d2Remarks: "",
    d3: "",
    d3Document: null,
    d4: "",
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [previewImages, setPreviewImages] = useState({
    d3Document: null,
  });

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const sectionData = localStorage.getItem("survey_section_d");
        if (sectionData) {
          const savedData = JSON.parse(sectionData);
          setForm(savedData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };
    loadSavedData();
  }, []);

  // LOGIC: Check if all 4 core questions are attempted
  const isFormSubmittable =
    !!form.d1 &&
    !!form.d3 &&
    !!form.d4 &&
    (form.d2.cleanUtensils ||
      form.d2.cleanCounters ||
      form.d2.longHandleSpoons ||
      form.d2.foodCovered ||
      form.d2.notAllConditions);

  // LOGIC: If 4 questions are done, it is NOT partial (returns false)
  const determinePartialStatus = () => {
    return !isFormSubmittable;
  };

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      const isAnyFieldFilled =
        Object.values(form).some(
          (v) => v !== "" && v !== null && typeof v !== "object",
        ) || Object.values(form.d2).some((v) => v === true);

      if (isAnyFieldFilled) {
        const isPartial = determinePartialStatus();

        const dataToSave = {
          ...form,
          isPartial: isPartial,
          lastSaved: new Date().toISOString(),
        };
        localStorage.setItem("survey_section_d", JSON.stringify(dataToSave));

        // Update the outer status in real-time
        const completedSections = JSON.parse(
          localStorage.getItem("completedSections") || "{}",
        );

        if (isFormSubmittable) {
          completedSections.d = true;
        } else {
          delete completedSections.d;
        }

        localStorage.setItem(
          "completedSections",
          JSON.stringify(completedSections),
        );
        setSaveStatus(t.global.alert_saved_locally);
        setTimeout(() => setSaveStatus(""), 2000);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [form, isFormSubmittable, t]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (name.startsWith("d2_")) {
      const field = name.replace("d2_", "");
      let updatedD2 = { ...form.d2, [field]: checked };

      if (field === "notAllConditions" && checked) {
        updatedD2 = {
          cleanUtensils: false,
          cleanCounters: false,
          longHandleSpoons: false,
          foodCovered: false,
          notAllConditions: true,
        };
      } else if (field !== "notAllConditions" && checked) {
        updatedD2.notAllConditions = false;
      }

      setForm((prev) => ({ ...prev, d2: updatedD2 }));
    } else if (type === "file") {
      const file = files[0];

      if (!file) return;

      // Size validation (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Type validation
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [name]: file }));

      // Image preview
      if (file.type.startsWith("image/")) {
        setPreviewImages((prev) => ({
          ...prev,
          [name]: URL.createObjectURL(file),
        }));
      }

      // Clear error
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const allHygieneConditionsMet =
    form.d2.cleanUtensils &&
    form.d2.cleanCounters &&
    form.d2.longHandleSpoons &&
    form.d2.foodCovered;

  const validateForm = () => {
    const newErrors = {};
    if (!form.d1?.trim()) newErrors.d1 = t.global.err_a1;
    const hasAnyConditionSelected = Object.values(form.d2).some(
      (v) => v === true,
    );
    if (!hasAnyConditionSelected)
      newErrors.d2 = "Please select at least one hygiene condition";
    if (!form.d3?.trim()) newErrors.d3 = t.global.err_a1;
    if (!form.d4?.trim())
      newErrors.d4 = t.global.err_a1;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const yesNoToInt = (value) =>
    value === t.global.yes ? 1 : value === t.global.no ? 2 : null;
  const ratingToInt = (value) => {
    const ratings = { 
      [t.global.rating_good]: 1, 
      [t.global.rating_satisfactory]: 2, 
      [t.global.rating_poor]: 3 
    };
    return ratings[value] || null;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("current_section", "genClean");
      payload.append("gen_clean_D1", yesNoToInt(form.d1));

      const d2String = Object.keys(form.d2)
        .filter((k) => form.d2[k])
        .join(",");
      payload.append("gen_clean_D2", d2String);
      payload.append("gen_clean_D2_remark", form.d2Remarks || "");
      payload.append("gen_clean_D3", yesNoToInt(form.d3));
      if (form.d3Document)
        payload.append("gen_clean_D3_upload", form.d3Document);
      payload.append("gen_clean_D4", ratingToInt(form.d4));

      const isPartial = determinePartialStatus();

      localStorage.setItem(
        "survey_section_d",
        JSON.stringify({ ...form, isPartial }),
      );

      // Update external status
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      if (isFormSubmittable) {
        completedSections.d = true;
      } else {
        delete completedSections.d;
      }
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      const partialSections = JSON.parse(
        localStorage.getItem("partialSections") || "{}",
      );
      if (isPartial) {
        partialSections.d = true;
      } else {
        delete partialSections.d;
      }
      localStorage.setItem("partialSections", JSON.stringify(partialSections));

      await dispatch(createCanteen(payload));
      navigate("/survey/sections", { state });
    } catch (err) {
      console.error("Error:", err);
      alert(t.global.alert_server_error);
      setIsSubmitting(false);
    }
  };

  const yesNoOptions = [t.global.yes, t.global.no];
  const ratingOptions = [t.global.rating_good, t.global.rating_satisfactory, t.global.rating_poor];

  const hygieneOptions = [
    {
      key: "cleanUtensils",
      label: t.modules.kiosk.sections.D.options.hygiene[0],
      icon: "🍽️",
    },
    {
      key: "cleanCounters",
      label: t.modules.kiosk.sections.D.options.hygiene[1],
      icon: "🧼",
    },
    {
      key: "longHandleSpoons",
      label: t.modules.kiosk.sections.D.options.hygiene[2],
      icon: "🥄",
    },
    {
      key: "foodCovered",
      label: t.modules.kiosk.sections.D.options.hygiene[3],
      icon: "🍲",
    },
    {
      key: "notAllConditions",
      label: t.modules.kiosk.sections.D.options.hygiene[4],
      icon: "⚠️",
    },
  ];

  const getSurveyTypeDisplay = (type) => {
    const types = {
      kiosk: t.global.survey_type_kiosk,
      kitchen: t.global.survey_type_kitchen,
      kitchencumDining: t.global.survey_type_kitchendining,
    };
    return types[type] || type || t.global.survey_type_default;
  };

  // UI state for warnings
  const willBePartial = !isFormSubmittable;
  const showCompletionStatus = isFormSubmittable;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kiosk.sections.D.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kiosk.sections.D.subtitle}
              </p>
              {saveStatus && (
                <div className="text-sm text-green-600 mt-1 flex items-center">
                  <CheckCircle size={14} className="mr-1" />
                  {saveStatus}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full flex items-center">
                <Building2 size={14} className="mr-1" />
                {getSurveyTypeDisplay(state?.surveyType)} {t.global.survey_type_default}
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          {/* Partial Submission Warning */}
          {showCompletionStatus && willBePartial && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="text-amber-500 mr-3 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-800">
                    Partial Submission
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    This section will be saved as{" "}
                    <span className="font-semibold">partially completed</span>{" "}
                    because:
                  </p>
                  <ul className="text-amber-700 text-sm mt-2 list-disc pl-5 space-y-1">
                    {form.d3 === t.global.yes && !form.d3Document && (
                      <li>Cleaning record document is missing ({t.global.optionalNote?.split(":")[0]})</li>
                    )}
                  </ul>
                  <p className="text-amber-700 text-sm mt-2">
                    You can submit now and complete later, or add the missing
                    optional information for full completion.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* API Error */}
          {apiError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="text-red-500 mr-3" size={20} />
              <div>
                <p className="font-medium text-red-800">{t.global.error_title}</p>
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* D1 - Adequate Space */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Home size={18} className="mr-2" />
                D1. {t.modules.kiosk.sections.D.questions.space}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.d1 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="d1"
                            value={option}
                            checked={form.d1 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.d1 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.d1 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d1}
                </p>
              )}
            </div>

            {/* D2 - Hygiene Conditions */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Sparkles size={18} className="mr-2" />
                D2. {t.modules.kiosk.sections.D.questions.hygiene}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kiosk.sections.D.selectAllHint || "Select all that apply:"}
              </p>

              <div className="space-y-2 mb-4">
                {hygieneOptions.map((option) => (
                  <label key={option.key} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 transition-all duration-200 flex items-center ${
                        form.d2[option.key]
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center flex-1">
                        <div className="relative mr-3">
                          {option.key === "notAllConditions" ? (
                            <>
                              <input
                                type="checkbox"
                                name={`d2_${option.key}`}
                                checked={form.d2[option.key]}
                                onChange={handleChange}
                                className="sr-only"
                                disabled={isSubmitting}
                              />
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  form.d2[option.key]
                                    ? "border-red-500 bg-red-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {form.d2[option.key] && (
                                  <div className="w-2 h-2 rounded bg-white"></div>
                                )}
                              </div>
                            </>
                          ) : (
                            <>
                              <input
                                type="checkbox"
                                name={`d2_${option.key}`}
                                checked={form.d2[option.key]}
                                onChange={handleChange}
                                className="sr-only"
                                disabled={
                                  isSubmitting || form.d2.notAllConditions
                                }
                              />
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  form.d2[option.key]
                                    ? "border-green-500 bg-green-500"
                                    : form.d2.notAllConditions
                                      ? "border-gray-200 bg-gray-100"
                                      : "border-gray-300"
                                }`}
                              >
                                {form.d2[option.key] && (
                                  <div className="w-2 h-2 rounded bg-white"></div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <span className="mr-2 text-lg">{option.icon}</span>
                        <span
                          className={`font-medium ${
                            form.d2.notAllConditions &&
                            option.key !== "notAllConditions"
                              ? "text-gray-400"
                              : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {errors.d2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d2}
                </p>
              )}

              {/* Remarks Section - Optional */}
              {(!allHygieneConditionsMet || form.d2.notAllConditions) && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1 text-gray-700">
                    {t.global.remarks_label}
                    <span className="text-gray-500 ml-1">
                      {t.global.optionalNote?.split(":")[0]}
                    </span>
                  </label>
                  <textarea
                    name="d2Remarks"
                    value={form.d2Remarks}
                    onChange={handleChange}
                    placeholder="Please specify which hygiene conditions are missing or any other gaps observed... (Optional)"
                    className={`w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.d2Remarks ? "border-red-500" : "border-gray-300"
                    }`}
                    rows={3}
                    disabled={isSubmitting}
                  />
                  {errors.d2Remarks && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />{" "}
                      {errors.d2Remarks}
                    </p>
                  )}
                </div>
              )}

              {/* Summary of selected conditions */}
              {!form.d2.notAllConditions &&
                (form.d2.cleanUtensils ||
                  form.d2.cleanCounters ||
                  form.d2.longHandleSpoons ||
                  form.d2.foodCovered) && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium mb-1">
                      Selected Conditions:
                    </p>
                    <ul className="text-sm text-green-700 list-disc pl-5">
                      {form.d2.cleanUtensils && (
                        <li>{t.modules.kiosk.sections.D.options.hygiene[0]}</li>
                      )}
                      {form.d2.cleanCounters && (
                        <li>{t.modules.kiosk.sections.D.options.hygiene[1]}</li>
                      )}
                      {form.d2.longHandleSpoons && (
                        <li>{t.modules.kiosk.sections.D.options.hygiene[2]}</li>
                      )}
                      {form.d2.foodCovered && (
                        <li>{t.modules.kiosk.sections.D.options.hygiene[3]}</li>
                      )}
                    </ul>
                    {allHygieneConditionsMet && (
                      <p className="mt-2 text-sm font-medium text-green-600 flex items-center">
                        <CheckCircle size={14} className="mr-1" /> All hygiene
                        conditions are met!
                      </p>
                    )}
                  </div>
                )}

              {form.d2.notAllConditions && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 font-medium flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    Not all hygiene conditions are met. Remarks are optional but
                    recommended.
                  </p>
                </div>
              )}
            </div>

            {/* D3 - Cleaning Record */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <ClipboardCheck size={18} className="mr-2" />
                D3. {t.modules.kiosk.sections.D.questions.cleaning}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.d3 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="d3"
                            value={option}
                            checked={form.d3 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.d3 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.d3 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d3}
                </p>
              )}
              {form.d3 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  <div>
                    <label className="block text-sm font-medium mb-3 text-gray-700">
                      Upload Cleaning Record ({t.global.optionalNote?.split(":")[0]})
                    </label>

                    {/* CAMERA + UPLOAD BUTTONS */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => setShowCamera(true)}
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Camera size={18} /> {t.global.use_camera}
                      </button>

                      <label
                        htmlFor="d3Document"
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Upload size={18} /> {t.global.upload_file}
                      </label>
                    </div>

                    <input
                      type="file"
                      name="d3Document"
                      id="d3Document"
                      accept="image/*,.pdf"
                      onChange={handleChange}
                      className="hidden"
                      disabled={isSubmitting}
                    />

                    {/* PREVIEW AREA */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        form.d3Document
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {form.d3Document ? (
                        <div className="space-y-4">
                          {/* IMAGE PREVIEW */}
                          {previewImages?.d3Document &&
                            form.d3Document.type.startsWith("image/") && (
                              <img
                                src={previewImages.d3Document}
                                alt="Preview"
                                className="max-h-48 mx-auto rounded-lg shadow"
                              />
                            )}

                          <div className="flex items-center justify-between">
                            <div className="text-left">
                              <p className="text-sm break-all">
                                {form.d3Document.name}
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                Type: {form.d3Document.type}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                if (previewImages?.d3Document) {
                                  URL.revokeObjectURL(previewImages.d3Document);
                                }

                                setForm((prev) => ({
                                  ...prev,
                                  d3Document: null,
                                }));

                                setPreviewImages((prev) => ({
                                  ...prev,
                                  d3Document: null,
                                }));
                              }}
                              className="text-red-600 text-xs font-bold"
                            >
                              {t.global.remove}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          {t.global.capture_hint} cleaning record
                        </p>
                      )}
                    </div>

                    {errors?.d3Document && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.d3Document}
                      </p>
                    )}
                  </div>

                  {!form.d3Document && (
                    <p className="text-xs text-amber-600">
                      Missing optional document — section will be partial
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* D4 - Overall Cleanliness */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Star size={18} className="mr-2" />
                D4. {t.modules.kiosk.sections.D.questions.overallCleanliness}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                {ratingOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.d4 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="d4"
                            value={option}
                            checked={form.d4 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.d4 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.d4 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d4}
                </p>
              )}
            </div>

            {/* Completion Status - Only show when form is submittable */}
            {showCompletionStatus && (
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Info size={18} className="mr-2 text-blue-600" />
                    <span className="font-medium text-gray-700">
                      Submission Status:
                    </span>
                  </div>
                  <div>
                    {willBePartial ? (
                      <span className="text-amber-600 font-medium">
                        Will be Partial
                      </span>
                    ) : (
                      <span className="text-green-600 font-medium">
                        Will be Complete
                      </span>
                    )}
                  </div>
                </div>

                {willBePartial && (
                  <div className="mt-2 text-sm text-amber-700">
                    <p>
                      Missing optional information will mark this section as
                      partially completed.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <div className="flex flex-col items-end">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isFormSubmittable}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                    isSubmitting || !isFormSubmittable
                      ? "bg-blue-400 text-white cursor-not-allowed"
                      : willBePartial
                        ? "bg-amber-500 text-white hover:bg-amber-600"
                        : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t.global.submitting}
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      {t.global.submit_section_d}
                    </>
                  )}
                </button>

                {isFormSubmittable && willBePartial && (
                  <p className="text-xs text-amber-600 mt-1 text-right">
                    Will be saved as partially completed
                  </p>
                )}
              </div>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-600 font-medium">
                  * Required fields
                </span>
                <span className="mx-2">•</span>
                <span className="text-amber-600 font-medium">
                  Optional fields
                </span>
                <span className="mx-2">•</span>
                <span className="text-gray-600">
                  Missing optional fields = Partial completion
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            if (!file) return;

            setForm((prev) => ({
              ...prev,
              d3Document: file,
            }));

            if (file.type.startsWith("image/")) {
              setPreviewImages((prev) => ({
                ...prev,
                d3Document: URL.createObjectURL(file),
              }));
            }

            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default SectionDKitchenDining;